/*
 *
 * App reducer
 *
 */

import { createReducer } from '@reduxjs/toolkit';
import {
  defaultAction,
  defaultActionSuccess,
  defaultActionError,
  toggleTheme,
  setAppLoaded,
  updateToken,
  logoutRequest,
  logoutSuccess,
  logoutError,
  clearSession,
  changeLanguage,
  saveLanguagePreference,
  saveLanguagePreferenceSuccess,
  saveLanguagePreferenceError,
} from './actions';
import { loginSuccess, User } from '../Auth/Login/actions';

export interface AppState {
  data: any;
  loading: boolean;
  error: string | null;
  isDarkMode: boolean;
  token: string | null;
  appLoaded: boolean;
  user: User | null;
  isAuthenticated: boolean;
  loggingOut: boolean;
  language: string;
}

export const initialState: AppState = {
  data: null,
  loading: false,
  error: null,
  isDarkMode: false,
  token: null,
  appLoaded: false,
  user: null,
  isAuthenticated: false,
  loggingOut: false,
  language: 'en',
};

const appReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(defaultAction, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(defaultActionSuccess, (state, action) => {
      state.loading = false;
      state.data = action.payload.data;
    })
    .addCase(defaultActionError, (state, action) => {
      state.loading = false;
      state.error = action.payload.error;
    })
    .addCase(toggleTheme, (state) => {
      state.isDarkMode = !state.isDarkMode;
    })
    .addCase(setAppLoaded, (state) => {
      state.appLoaded = true;
    })
    .addCase(loginSuccess, (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.token = action.payload.token;
    })
    .addCase(updateToken, (state, action) => {
      state.token = action.payload;
    })
    .addCase(logoutRequest, (state) => {
      state.loggingOut = true;
      state.error = null;
    })
    .addCase(logoutSuccess, (state) => {
      state.loggingOut = false;
      state.user = null;
      state.isAuthenticated = false;
      state.token = null;
      state.data = null;
      state.error = null;
    })
    .addCase(logoutError, (state, action) => {
      state.loggingOut = false;
      state.error = action.payload.error;
      state.user = null;
      state.isAuthenticated = false;
      state.token = null;
      state.data = null;
    })
    .addCase(clearSession, (state) => {
      // Clear authentication state without setting loggingOut flag
      state.user = null;
      state.isAuthenticated = false;
      state.token = null;
      state.data = null;
      state.error = null;
    })
    .addCase(changeLanguage, (state, action) => {
      state.language = action.payload.language;
    })
    .addCase(saveLanguagePreference, (state) => {
      // Loading state could be added here if needed
    })
    .addCase(saveLanguagePreferenceSuccess, (state) => {})
    .addCase(saveLanguagePreferenceError, (state, action) => {
      state.error = action.payload.error;
    });
});

export default appReducer;
